import { useEffect, useState } from 'react'
import {
  getAcceleratorPriceState,
  type AcceleratorPriceState,
} from '../lib/acceleratorPricing'

/**
 * Live Accelerator price state for the page.
 *
 * WHERE THE TIME COMES FROM
 * -------------------------
 * The device clock is not trusted on its own. On mount this asks
 * /api/acceleratorPricing (getAcceleratorPricing) for the server's time and
 * keeps the difference as an offset, so a visitor whose clock is wrong still
 * sees the regular price at Mon Nov 30 11:59:59 PM ET. If that call fails the
 * device clock is used, which is right for nearly everyone, and checkout is
 * enforced on the server either way (submitAcceleratorLead).
 *
 * The state is re-evaluated every second while the countdown shows and every
 * 30 seconds otherwise, so an open page flips to the regular price on its own.
 *
 * activeClassStartMs comes from the caller (useNextClassDate). undefined means
 * "not loaded yet"; the server's copy is used as a fallback if it arrives first.
 */

export const PRICING_ENDPOINT = '/api/acceleratorPricing'

interface ServerPricing {
  serverNowMs: number
  activeClassStartMs: number | null
}

function parseServerPricing(body: unknown): ServerPricing | null {
  if (!body || typeof body !== 'object') return null
  const b = body as Record<string, unknown>
  if (typeof b.serverNowMs !== 'number' || !Number.isFinite(b.serverNowMs)) return null
  const start = b.activeClassStartMs
  if (start !== null && (typeof start !== 'number' || !Number.isFinite(start))) return null
  return { serverNowMs: b.serverNowMs, activeClassStartMs: start as number | null }
}

export function useAcceleratorPricing(
  activeClassStartMs: number | null | undefined,
  options: { serverClock?: boolean } = {},
): AcceleratorPriceState {
  // Home and /start only show a one-line price; they skip the server call so
  // those pages add no function invocation. /accelerator always uses it.
  const serverClock = options.serverClock !== false
  const [offsetMs, setOffsetMs] = useState(0)
  const [serverClassStartMs, setServerClassStartMs] = useState<number | null | undefined>(undefined)
  const [nowMs, setNowMs] = useState(() => Date.now())

  useEffect(() => {
    if (!serverClock) return
    let cancelled = false
    const sentAt = Date.now()
    fetch(PRICING_ENDPOINT, { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((body: unknown) => {
        const parsed = parseServerPricing(body)
        if (cancelled || !parsed) return
        const receivedAt = Date.now()
        // Midpoint of the round trip is the best local match for serverNowMs.
        setOffsetMs(parsed.serverNowMs - (sentAt + receivedAt) / 2)
        setServerClassStartMs(parsed.activeClassStartMs)
      })
      .catch(() => {
        // Offline, local preview, endpoint not deployed. Device clock it is.
      })
    return () => {
      cancelled = true
    }
  }, [serverClock])

  const classStartMs = activeClassStartMs !== undefined ? activeClassStartMs : serverClassStartMs
  const correctedNow = nowMs + offsetMs
  const state = getAcceleratorPriceState(correctedNow, classStartMs)

  const tickMs = state.showCountdown ? 1000 : 30000
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), tickMs)
    return () => window.clearInterval(id)
  }, [tickMs])

  return state
}
